/**
 * Security utilities for production deployment
 */

/**
 * Check if the app is running in production
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD;
};

/**
 * Check if the app is served over HTTPS
 */
export const isSecureContext = (): boolean => {
  return window.isSecureContext;
};

/**
 * Validate that the app is running in a secure context in production
 */
export const validateSecureContext = (): void => {
  if (isProduction() && !isSecureContext()) {
    console.error('🚨 Security Warning: App is running in production without HTTPS!');
    // In a real app, you might want to redirect to HTTPS or show a warning
  }
};

/**
 * Content Security Policy violation handler
 */
export const setupCSPViolationReporting = (): void => {
  if (typeof document !== 'undefined') {
    document.addEventListener('securitypolicyviolation', (event) => {
      console.warn('🛡️ CSP Violation:', {
        blockedURI: event.blockedURI,
        documentURI: event.documentURI,
        effectiveDirective: event.effectiveDirective,
        originalPolicy: event.originalPolicy,
        referrer: event.referrer,
        statusCode: event.statusCode,
        violatedDirective: event.violatedDirective
      });
      
      // In production, you might want to send this to your monitoring service
      if (isProduction()) {
        // Send to Sentry or another monitoring service
        // Sentry.captureMessage('CSP Violation', { extra: event });
      }
    });
  }
};

/**
 * Security headers that should be present in production
 */
export const REQUIRED_SECURITY_HEADERS = [
  'strict-transport-security',
  'x-frame-options',
  'x-content-type-options',
  'content-security-policy',
  'referrer-policy'
] as const;

/**
 * Check if required security headers are present (client-side detection is limited)
 */
export const checkSecurityHeaders = async (): Promise<void> => {
  if (!isProduction()) return;
  
  try {
    // This is a basic check - in reality, headers are better checked server-side
    const response = await fetch(window.location.href, { 
      method: 'HEAD',
      cache: 'no-cache'
    });
    
    const headers = response.headers;
    const missingHeaders: string[] = [];
    
    REQUIRED_SECURITY_HEADERS.forEach(header => {
      if (!headers.has(header)) {
        missingHeaders.push(header);
      }
    });
    
    if (missingHeaders.length > 0) {
      console.warn('🛡️ Missing security headers:', missingHeaders);
    }
  } catch (error) {
    console.error('Failed to check security headers:', error);
  }
};

/**
 * Initialize security checks
 */
export const initSecurity = (): void => {
  validateSecureContext();
  setupCSPViolationReporting();
  
  if (isProduction()) {
    // Run security checks after a delay to avoid blocking initial load
    setTimeout(checkSecurityHeaders, 2000);
  }
};

/**
 * Sanitize user input to prevent XSS
 */
export const sanitizeInput = (input: string): string => {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
};

/**
 * Validate if a URL is safe for redirects
 */
export const isSafeUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    
    // Only allow same-origin URLs or trusted domains
    const trustedDomains = [
      window.location.hostname,
      'smtjbmylhvcxhnigwsji.supabase.co' // Supabase domain
    ];
    
    return trustedDomains.includes(parsedUrl.hostname);
  } catch {
    return false;
  }
};